import React, { useEffect, useMemo, useState } from 'react';
import {
    Avatar,
    Modal,
    Box,
    Button,
    CircularProgress,
    LinearProgress,
    MenuItem,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import {
    ArrowBack,
    ArrowForward,
    CheckCircle,
    CloudUpload,
    SkipNext,
    SmartToy,
} from '@mui/icons-material';
import { useSetAtom } from 'jotai';

import { DEFAULT_CONFIG } from '../../models/data/ChatbotConfig';
import { chatbotConfigState } from '../../state/config/ChatbotConfigState';
import { saveChatbotConfig, uploadLogo } from '../../utils/server/storageUtils';
import { showToastError, showToastSuccess } from '../../utils/common/toast';
import { useAuth } from '../../../context/AuthContext';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import wallpaper1 from '../../../assets/wallpaper-1.png';
import wallpaper2 from '../../../assets/wallpaper-2.png';
import wallpaper3 from '../../../assets/wallpaper-3.png';

const FALLBACK_PRIMARY = '#172983';
const FALLBACK_SECONDARY = '#068945';
const FALLBACK_NAME = '';
const FALLBACK_FONT = 'Roboto';
const FALLBACK_LANGUAGE = 'de';
const FALLBACK_WALLPAPER = 'none';

const LANGUAGE_OPTIONS = [
    { value: 'de', labelKey: 'languageOptionGerman' },
    { value: 'en', labelKey: 'languageOptionEnglish' },
];

const FONT_OPTIONS = [
    'Roboto',
    'Inter',
    'Poppins',
    'Open Sans',
    'Lato',
    'Nunito',
    'Montserrat',
    'DM Sans',
    'Work Sans',
    'Source Sans Pro',
    'Fira Sans',
    'Ubuntu',
    'Segoe UI',
    'Helvetica Neue',
    'System UI',
    'Arial',
    'Verdana',
    'Trebuchet MS',
    'Tahoma',
    'Georgia',
    'Merriweather',
    'Playfair Display',
    'Times New Roman',
    'Courier New',
];

const SYSTEM_FONTS = [
    'Arial',
    'Courier New',
    'Georgia',
    'Helvetica Neue',
    'Segoe UI',
    'System UI',
    'Tahoma',
    'Times New Roman',
    'Trebuchet MS',
    'Verdana',
];

const CHAT_WALLPAPER_OPTIONS = [
    {
        value: 'wallpaper_1',
        labelKey: 'wallpaper1Label',
        descriptionKey: 'wallpaper1Desc',
        assetPath: wallpaper1,
        fallback: 'linear-gradient(135deg, rgba(59,130,246,0.28) 0%, rgba(99,102,241,0.2) 40%, rgba(255,255,255,0.18) 100%)',
    },
    {
        value: 'wallpaper_2',
        labelKey: 'wallpaper2Label',
        descriptionKey: 'wallpaper2Desc',
        assetPath: wallpaper2,
        fallback: 'linear-gradient(135deg, rgba(16,185,129,0.24) 0%, rgba(6,182,212,0.18) 45%, rgba(255,255,255,0.16) 100%)',
    },
    {
        value: 'wallpaper_3',
        labelKey: 'wallpaper3Label',
        descriptionKey: 'wallpaper3Desc',
        assetPath: wallpaper3,
        fallback: 'linear-gradient(135deg, rgba(244,114,182,0.22) 0%, rgba(168,85,247,0.18) 48%, rgba(255,255,255,0.16) 100%)',
    },
    {
        value: 'none',
        labelKey: 'wallpaperNoneLabel',
        descriptionKey: 'wallpaperNoneDesc',
        assetPath: '',
        fallback: 'linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(241,245,249,0.96) 100%)',
        isPlain: true,
    },
];

function getWallpaperOption(value) {
    return CHAT_WALLPAPER_OPTIONS.find((item) => item.value === value) || CHAT_WALLPAPER_OPTIONS[CHAT_WALLPAPER_OPTIONS.length - 1];
}

function getWallpaperPreviewStyles(value, dimmed = false) {
    const option = getWallpaperOption(value);
    const overlay = dimmed ? 'linear-gradient(rgba(255,255,255,0.10), rgba(255,255,255,0.10))' : 'linear-gradient(rgba(255,255,255,0), rgba(255,255,255,0))';

    if (option.isPlain || !option.assetPath) {
        return {
            backgroundImage: `${overlay}, ${option.fallback}`,
            backgroundSize: 'auto, cover',
            backgroundPosition: 'center, center',
        };
    }

    return {
        backgroundImage: `${overlay}, url("${option.assetPath}"), ${option.fallback}`,
        backgroundSize: 'auto, cover, cover',
        backgroundPosition: 'center, center, center',
    };
}

function getDefaultWizardConfig(initialConfig = {}) {
    return {
        ...DEFAULT_CONFIG,
        modelType: 'fast',
        chatbotName: FALLBACK_NAME,
        language: FALLBACK_LANGUAGE,
        primaryColor: FALLBACK_PRIMARY,
        secondaryColor: FALLBACK_SECONDARY,
        fontFamily: FALLBACK_FONT,
        logoUrl: '',
        chatWallpaper: FALLBACK_WALLPAPER,
        ...initialConfig,
    };
}

function ReviewRow({ label, children, stacked = false }) {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: stacked ? '1fr' : '112px minmax(0, 1fr)',
                alignItems: stacked ? 'stretch' : 'center',
                gap: stacked ? 0.9 : 1.25,
            }}
        >
            <Typography
                sx={{
                    color: 'var(--text-strong)',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                }}
            >
                {label}
            </Typography>
            <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flexWrap: 'wrap', gap: 1 }}>{children}</Box>
        </Box>
    );
}

export function ChatbotSetupWizard({ open, initialConfig, onCompleted }) {
    const { token } = useAuth();
    const setGlobalConfig = useSetAtom(chatbotConfigState);

    const { t } = useAppTranslation();

    const tr = (key, vars = {}) => {
        const template = (t && t[key]) ? t[key] : key;
        return String(template).replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : ''));
    };

    const [activeStep, setActiveStep] = useState(0);
    const [draft, setDraft] = useState(() => getDefaultWizardConfig(initialConfig));
    const [isSaving, setIsSaving] = useState(false);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    const [showDoneScreen, setShowDoneScreen] = useState(false);

    const stepTitles = useMemo(() => ([
        t.wizardStepTitleWelcome,
        t.wizardStepTitleName,
        t.wizardStepTitleLanguage,
        t.wizardStepTitleColors,
        t.wizardStepTitleFont,
        t.wizardStepTitleLogo,
        t.wizardStepTitleWallpaper,
        t.wizardStepTitleReview,
    ]), [t]);

    useEffect(() => {
        if (!open) return;

        const nextDraft = getDefaultWizardConfig(initialConfig || {});
        setDraft(nextDraft);
        setActiveStep(0);
        setShowDoneScreen(false);
    }, [open, initialConfig]);

    // Keep the global state in sync with the draft
    useEffect(() => {
        if (!open) return;
        setGlobalConfig(draft);
    }, [draft, open, setGlobalConfig]);

    useEffect(() => {
        const fontName = draft.fontFamily;
        if (!fontName || SYSTEM_FONTS.includes(fontName)) return;

        const linkId = 'wizard-font-preview-link';
        const formattedName = fontName.replace(/\s+/g, '+');
        const googleFontUrl = `https://fonts.googleapis.com/css2?family=${formattedName}:wght@300;400;500;700&display=swap`;

        let link = document.getElementById(linkId);
        if (!link) {
            link = document.createElement('link');
            link.id = linkId;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        }
        link.href = googleFontUrl;
    }, [draft.fontFamily]);

    const completionRatio = useMemo(() => ((activeStep + 1) / stepTitles.length) * 100, [activeStep]);

    const updateDraft = (field, value) => {
        setDraft((prev) => ({ ...prev, [field]: value }));
    };

    const goNext = () => {
        setActiveStep((prev) => Math.min(prev + 1, stepTitles.length - 1));
    };

    const goBack = () => {
        setActiveStep((prev) => Math.max(prev - 1, 0));
    };

    const handleSkip = () => {
        if (activeStep === stepTitles.length - 1) {
            finishSetup();
            return;
        }
        goNext();
    };

    const handleLogoUpload = async (event) => {
        if (!event.target.files || event.target.files.length === 0) return;

        const file = event.target.files[0];

        if (file.size > 2 * 1024 * 1024) {
            showToastError(t.errorFileTooBig);
            event.target.value = '';
            return;
        }

        if (!token) {
            showToastError(t.pleaseLoginAgain);
            event.target.value = '';
            return;
        }

        setIsUploadingLogo(true);

        try {
            const formData = new FormData();
            formData.append('file', file);
            const downloadUrl = await uploadLogo(formData, token);
            updateDraft('logoUrl', downloadUrl);
            showToastSuccess(t.successLogo);
        } catch (error) {
            console.error('Wizard logo upload error:', error);
            showToastError(error?.message || t.errorUploadFailed);
        } finally {
            setIsUploadingLogo(false);
            event.target.value = '';
        }
    };

    const finishSetup = async () => {
        if (!token) {
            showToastError(t.pleaseLoginAgain);
            return;
        }

        setIsSaving(true);

        try {
            const configToSave = {
                ...DEFAULT_CONFIG,
                ...draft,
                modelType: draft.modelType || 'fast',
                chatbotName: draft.chatbotName || t.defaultChatbotName,
                language: draft.language || FALLBACK_LANGUAGE,
                primaryColor: draft.primaryColor || FALLBACK_PRIMARY,
                secondaryColor: draft.secondaryColor || FALLBACK_SECONDARY,
                fontFamily: draft.fontFamily || FALLBACK_FONT,
                chatWallpaper: draft.chatWallpaper || FALLBACK_WALLPAPER,
                hasCompletedOnboarding: true,
                onboardingCompletedAt: new Date().toISOString(),
                setupSource: 'chatbot-first-run',
            };

            const saveResult = await saveChatbotConfig(token, configToSave);
            const persistedConfig = {
                ...configToSave,
                ...(saveResult?.config || {}),
                hasCompletedOnboarding: Boolean(saveResult?.config?.hasCompletedOnboarding ?? configToSave.hasCompletedOnboarding),
                onboardingCompletedAt: saveResult?.config?.onboardingCompletedAt || configToSave.onboardingCompletedAt,
                setupSource: saveResult?.config?.setupSource || configToSave.setupSource,
            };

            setGlobalConfig(persistedConfig);
            setDraft(persistedConfig);
            setShowDoneScreen(true);
            showToastSuccess(t.wizardDoneBody);
        } catch (error) {
            console.error('Wizard save error:', error);
            showToastError(error?.message || t.errorSaved);
        } finally {
            setIsSaving(false);
        }
    };

    const handleStartChat = () => {
        if (typeof onCompleted === 'function') {
            onCompleted({
                ...draft,
                hasCompletedOnboarding: true,
                onboardingCompletedAt: draft.onboardingCompletedAt || new Date().toISOString(),
                setupSource: draft.setupSource || 'chatbot-first-run',
            });
        }
    };

    if (!open) return null;

    const selectMenuProps = {
        disableScrollLock: true,
        sx: {
            zIndex: (theme) => theme.zIndex.modal + 30,
        },
        PaperProps: {
            sx: {
                mt: 1,
                borderRadius: '16px',
                border: '1px solid var(--border-color)',
                boxShadow: '0 20px 48px rgba(15, 23, 42, 0.14)',
                maxHeight: 'min(34vh, 220px)',
                overflowY: 'auto',
                overscrollBehavior: 'contain',
            },
        },
        MenuListProps: {
            sx: {
                py: 0.5,
            },
        },
    };

    const previewName = draft.chatbotName || t.defaultChatbotName;
    const previewPrimary = draft.primaryColor || FALLBACK_PRIMARY;
    const previewSecondary = draft.secondaryColor || FALLBACK_SECONDARY;
    const previewFont = draft.fontFamily || FALLBACK_FONT;
    const previewLanguage =
        t[LANGUAGE_OPTIONS.find((option) => option.value === (draft.language || FALLBACK_LANGUAGE))?.labelKey] ||
        (draft.language || FALLBACK_LANGUAGE);
    const previewWallpaper = getWallpaperOption(draft.chatWallpaper || FALLBACK_WALLPAPER);

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant='overline' sx={{ color: 'var(--text-muted)', letterSpacing: '0.12em', fontWeight: 800 }}>
                            {t.wizardIntroTag}
                        </Typography>
                        <Typography variant='h4' sx={{ fontWeight: 800, color: 'var(--text-strong)', lineHeight: 1.15 }}>
                            {t.wizardIntroHeadline}
                        </Typography>
                        <Typography sx={{ color: 'var(--text-muted)', lineHeight: 1.8 }}>
                            {t.wizardIntroBody}
                        </Typography>
                    </Box>
                );

            case 1:
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant='h5' sx={{ fontWeight: 800, color: 'var(--text-strong)' }}>
                            {t.wizardNameHeadline}
                        </Typography>
                        <Typography sx={{ color: 'var(--text-muted)' }}>
                            {t.wizardNameBody}
                        </Typography>
                        <TextField
                            label={t.chatbotName}
                            value={draft.chatbotName || ''}
                            onChange={(e) => updateDraft('chatbotName', e.target.value)}
                            fullWidth
                        />
                    </Box>
                );

            case 2:
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant='h5' sx={{ fontWeight: 800, color: 'var(--text-strong)' }}>
                            {t.wizardLanguageHeadline}
                        </Typography>
                        <Typography sx={{ color: 'var(--text-muted)' }}>
                            {t.wizardLanguageBody}
                        </Typography>
                        <TextField
                            select
                            label={t.language}
                            value={draft.language || FALLBACK_LANGUAGE}
                            onChange={(e) => updateDraft('language', e.target.value)}
                            fullWidth
                            SelectProps={{ MenuProps: selectMenuProps }}
                        >
                            {LANGUAGE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {t[option.labelKey]}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                );

            case 3:
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant='h5' sx={{ fontWeight: 800, color: 'var(--text-strong)' }}>
                            {t.wizardColorsHeadline}
                        </Typography>
                        <Typography sx={{ color: 'var(--text-muted)' }}>
                            {t.wizardColorsBody}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            <TextField
                                label={t.primaryColor}
                                type='color'
                                value={previewPrimary}
                                onChange={(e) => updateDraft('primaryColor', e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                sx={{ '& input': { height: 52, cursor: 'pointer' } }}
                            />
                            <TextField
                                label={t.secondaryColor}
                                type='color'
                                value={previewSecondary}
                                onChange={(e) => updateDraft('secondaryColor', e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                sx={{ '& input': { height: 52, cursor: 'pointer' } }}
                            />
                        </Box>
                    </Box>
                );

            case 4:
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant='h5' sx={{ fontWeight: 800, color: 'var(--text-strong)' }}>
                            {t.wizardFontHeadline}
                        </Typography>
                        <Typography sx={{ color: 'var(--text-muted)' }}>
                            {t.wizardFontBody}
                        </Typography>
                        <TextField
                            select
                            label={t.fontFamily}
                            value={previewFont}
                            onChange={(e) => updateDraft('fontFamily', e.target.value)}
                            fullWidth
                            SelectProps={{
                                MenuProps: selectMenuProps,
                                sx: { 
                                    fontFamily: previewFont,
                                    '& .MuiSelect-select': { fontFamily: previewFont }
                                }
                            }}
                        >
                            {FONT_OPTIONS.map((font) => (
                                <MenuItem 
                                    key={font} 
                                    value={font} 
                                    style={{ fontFamily: font }}
                                >
                                    {font}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                );

            case 5:
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant='h5' sx={{ fontWeight: 800, color: 'var(--text-strong)' }}>
                            {t.wizardLogoHeadline}
                        </Typography>
                        <Typography sx={{ color: 'var(--text-muted)' }}>
                            {t.wizardLogoBody}
                        </Typography>
                        <Button
                            component='label'
                            variant='outlined'
                            startIcon={isUploadingLogo ? <CircularProgress size={18} /> : <CloudUpload />}
                            disabled={isUploadingLogo}
                            sx={{
                                minHeight: 56,
                                borderRadius: '16px',
                                textTransform: 'none',
                                fontWeight: 700,
                                borderColor: 'var(--border-color)',
                                color: 'var(--text-strong)',
                                backgroundColor: 'var(--surface-fill-strong)',
                                '&:hover': {
                                    borderColor: 'var(--accent-color)',
                                    backgroundColor: 'var(--surface-muted)',
                                },
                            }}
                        >
                            {isUploadingLogo ? t.uploading : t.logoUpload}
                            <input hidden type='file' accept='image/*' onChange={handleLogoUpload} />
                        </Button>
                        {draft.logoUrl && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1.5,
                                    borderRadius: '16px',
                                    border: '1px solid var(--border-color)',
                                    backgroundColor: 'var(--surface-muted)',
                                }}
                            >
                                <Avatar src={draft.logoUrl} alt={t.preview} sx={{ width: 56, height: 56 }} />
                                <Typography sx={{ color: 'var(--text-soft)', fontWeight: 700 }}>{t.wizardLogoReady}</Typography>
                            </Box>
                        )}
                    </Box>
                );

            case 6:
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant='h5' sx={{ fontWeight: 800, color: 'var(--text-strong)' }}>
                            {t.wizardWallpaperHeadline}
                        </Typography>
                        <Typography sx={{ color: 'var(--text-muted)' }}>
                            {t.wizardWallpaperBody}
                        </Typography>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                gap: 1.5,
                            }}
                        >
                            {CHAT_WALLPAPER_OPTIONS.map((option) => {
                                const isSelected = (draft.chatWallpaper || FALLBACK_WALLPAPER) === option.value;
                                return (
                                    <Button
                                        key={option.value}
                                        type='button'
                                        onClick={() => updateDraft('chatWallpaper', option.value)}
                                        variant='outlined'
                                        sx={{
                                            p: 0,
                                            borderRadius: '18px',
                                            textTransform: 'none',
                                            overflow: 'hidden',
                                            alignItems: 'stretch',
                                            justifyContent: 'stretch',
                                            borderColor: isSelected ? 'var(--accent-color)' : 'var(--border-color)',
                                            boxShadow: isSelected ? '0 0 0 3px var(--accent-glow)' : 'none',
                                            '&:hover': {
                                                borderColor: 'var(--accent-color)',
                                                backgroundColor: 'transparent',
                                            },
                                        }}
                                    >
                                        <Box sx={{ width: '100%' }}>
                                            <Box
                                                sx={{
                                                    height: { xs: 88, sm: 112 },
                                                    ...getWallpaperPreviewStyles(option.value),
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    px: 1.4,
                                                    py: 1.2,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    backgroundColor: 'var(--surface-fill-strong)',
                                                }}
                                            >
                                                <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                                                    <Typography sx={{ fontWeight: 800, color: 'var(--text-strong)' }}>
                                                        {t[option.labelKey]}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                                        {t[option.descriptionKey]}
                                                    </Typography>
                                                </Box>
                                                {isSelected && (
                                                    <CheckCircle sx={{ color: 'var(--accent-color)', flexShrink: 0 }} />
                                                )}
                                            </Box>
                                        </Box>
                                    </Button>
                                );
                            })}
                        </Box>
                    </Box>
                );

            default:
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant='h5' sx={{ fontWeight: 800, color: 'var(--text-strong)' }}>
                            {t.wizardReviewHeadline}
                        </Typography>
                        <Typography sx={{ color: 'var(--text-muted)' }}>
                            {t.wizardReviewBody}
                        </Typography>

                        <Box
                            sx={{
                                p: { xs: 1.6, sm: 2 },
                                borderRadius: '20px',
                                border: '1px solid var(--border-color)',
                                backgroundColor: 'var(--surface-fill-strong)',
                                display: 'grid',
                                gap: 1.5,
                            }}
                        >
                            <ReviewRow label={t.wizardReviewName}>
                                <Typography sx={{ color: 'var(--text-strong)', fontWeight: 600 }} noWrap>
                                    {previewName}
                                </Typography>
                            </ReviewRow>

                            <ReviewRow label={t.wizardReviewLanguage}>
                                <Typography sx={{ color: 'var(--text-soft)', fontWeight: 600 }}>
                                    {previewLanguage}
                                </Typography>
                            </ReviewRow>

                            <ReviewRow label={t.wizardReviewPrimary}>
                                <Box
                                    sx={{
                                        width: 86,
                                        height: 34,
                                        borderRadius: '999px',
                                        border: '1px solid var(--border-color)',
                                        backgroundColor: previewPrimary,
                                        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.35)',
                                    }}
                                />
                            </ReviewRow>

                            <ReviewRow label={t.wizardReviewSecondary}>
                                <Box
                                    sx={{
                                        width: 86,
                                        height: 34,
                                        borderRadius: '999px',
                                        border: '1px solid var(--border-color)',
                                        backgroundColor: previewSecondary,
                                        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.35)',
                                    }}
                                />
                            </ReviewRow>

                            <ReviewRow label={t.wizardReviewFont}>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        px: 1.5,
                                        py: 0.9,
                                        borderRadius: '999px',
                                        border: '1px solid var(--border-color)',
                                        backgroundColor: 'var(--surface-muted)',
                                        color: 'var(--text-strong)',
                                        fontWeight: 700,
                                        fontFamily: previewFont,
                                        maxWidth: '100%',
                                    }}
                                >
                                    <Box
                                        component='span'
                                        sx={{
                                            display: 'inline-block',
                                            minWidth: 0,
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {previewFont}
                                    </Box>
                                </Box>
                            </ReviewRow>

                            <ReviewRow label={t.wizardReviewLogo}>
                                <Avatar
                                    src={draft.logoUrl || ''}
                                    alt='Selected logo'
                                    sx={{
                                        width: 58,
                                        height: 58,
                                        border: '2px solid var(--border-color)',
                                        bgcolor: 'var(--surface-muted)',
                                    }}
                                >
                                    <SmartToy sx={{ color: 'var(--text-muted)' }} />
                                </Avatar>
                            </ReviewRow>

                            <ReviewRow label={t.wizardReviewWallpaper} stacked>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: 88,
                                        borderRadius: '16px',
                                        border: '1px solid var(--border-color)',
                                        overflow: 'hidden',
                                        ...getWallpaperPreviewStyles(previewWallpaper.value),
                                    }}
                                />
                                <Typography sx={{ mt: 0.75, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                                    {t[previewWallpaper.labelKey]} • {t[previewWallpaper.descriptionKey]}
                                </Typography>
                            </ReviewRow>
                        </Box>
                    </Box>
                );
        }
    };

    return (
        <Modal
            open={open}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(2, 6, 23, 0.58)',
                        backdropFilter: 'blur(12px)',
                    },
                },
            }}
            sx={{
                zIndex: (theme) => theme.zIndex.modal + 10,
                display: 'flex',
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'center',
                px: { xs: 1.25, sm: 2 },
                py: { xs: 1.25, sm: 2 },
                overflowY: 'auto',
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    maxWidth: 1120,
                    maxHeight: { xs: '92dvh', md: '92vh' },
                    borderRadius: { xs: '24px', md: '32px' },
                    overflow: 'hidden',
                    border: '1px solid var(--border-color)',
                    background: 'var(--surface-gradient)',
                    boxShadow: '0 36px 80px rgba(15, 23, 42, 0.36)',
                    outline: 'none', 
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '0.95fr 1.05fr' },
                        minHeight: 0,
                    }}
                >
                    <Box
                        sx={{
                            p: { xs: 3, md: 4 },
                            color: '#ffffff',
                            background: `radial-gradient(circle at top left, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 34%), linear-gradient(145deg, ${previewPrimary} 0%, #0f172a 55%, ${previewSecondary} 100%)`,
                            display: { xs: 'none', md: 'flex' },
                            flexDirection: 'column',
                            gap: 2.5,
                            minHeight: { md: 680 },
                        }}
                    >
                        <Box>
                            <Typography variant='overline' sx={{ opacity: 0.92, letterSpacing: '0.12em', fontWeight: 800 }}>
                                {tr('wizardProgress', { current: showDoneScreen ? stepTitles.length : activeStep + 1, total: stepTitles.length })}
                            </Typography>
                            <Typography variant='h4' sx={{ mt: 1, fontWeight: 800, lineHeight: 1.1 }}>
                                {showDoneScreen ? t.wizardLeftDoneTitle : t.wizardLeftTitle}
                            </Typography>
                            <Typography sx={{ mt: 1.25, opacity: 0.86, lineHeight: 1.7 }}>
                                {showDoneScreen
                                    ? t.wizardLeftDoneBody
                                    : t.wizardLeftBody}
                            </Typography>
                        </Box>

                        <Box>
                            <LinearProgress
                                variant='determinate'
                                value={showDoneScreen ? 100 : completionRatio}
                                sx={{
                                    height: 10,
                                    borderRadius: '999px',
                                    backgroundColor: 'rgba(255,255,255,0.18)',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: '999px',
                                        backgroundColor: 'var(--surface-fill-strong)',
                                    },
                                }}
                            />
                        </Box>

                        <Box
                            sx={{
                                mt: 1,
                                p: 2.5,
                                borderRadius: '28px',
                                border: '1px solid rgba(255,255,255,0.16)',
                                backgroundColor: 'rgba(255,255,255,0.08)',
                                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14)',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <Avatar
                                    src={draft.logoUrl}
                                    alt={t.previewLogoAlt}
                                    sx={{
                                        width: 58,
                                        height: 58,
                                        border: '2px solid rgba(255,255,255,0.88)',
                                        bgcolor: 'rgba(255,255,255,0.18)',
                                    }}
                                >
                                    <SmartToy />
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 800, fontFamily: previewFont }} noWrap>
                                        {previewName}
                                    </Typography>
                                    <Typography sx={{ opacity: 0.8, fontSize: '0.88rem', fontFamily: previewFont }}>
                                        {t.wizardPreviewSubtitle}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    p: 1.75,
                                    borderRadius: '18px',
                                    border: '1px solid rgba(255,255,255,0.14)',
                                    ...getWallpaperPreviewStyles(draft.chatWallpaper || FALLBACK_WALLPAPER, true),
                                }}
                            >
                                <Box
                                    sx={{
                                        p: 1.2,
                                        borderRadius: '16px',
                                        backgroundColor: 'rgba(255,255,255,0.74)',
                                        backdropFilter: 'blur(3px)',
                                        border: '1px solid rgba(255,255,255,0.24)',
                                    }}
                                >
                                    <Typography sx={{ fontFamily: previewFont, color: '#0f172a', lineHeight: 1.7 }}>
                                        {t.wizardPreviewMessage}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Box sx={{ flex: 1, height: 44, borderRadius: '14px', backgroundColor: previewPrimary }} />
                            <Box sx={{ flex: 1, height: 44, borderRadius: '14px', backgroundColor: previewSecondary }} />
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            p: { xs: 2, sm: 3, md: 4 },
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                            maxHeight: { xs: '92dvh', md: 'none' },
                            overflowY: { xs: 'auto', md: 'visible' },
                            overscrollBehavior: 'contain',
                        }}
                    >
                        {showDoneScreen ? (
                            <Box
                                sx={{
                                    minHeight: { xs: 'auto', md: 600 },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: { xs: 'flex-start', md: 'center' },
                                    textAlign: { xs: 'left', md: 'center' },
                                    gap: 2,
                                }}
                            >
                                <CheckCircle sx={{ fontSize: 72, color: '#16a34a' }} />
                                <Typography variant='h4' sx={{ fontWeight: 800, color: 'var(--text-strong)' }}>
                                    {t.wizardDoneHeadline}
                                </Typography>
                                <Typography sx={{ color: 'var(--text-muted)', maxWidth: 420, lineHeight: 1.8 }}>
                                    {t.wizardDoneBody}
                                </Typography>
                                <Button
                                    type='button'
                                    onClick={handleStartChat}
                                    variant='contained'
                                    size='large'
                                    sx={{
                                        mt: 1,
                                        minHeight: 54,
                                        px: 3,
                                        borderRadius: '16px',
                                        textTransform: 'none',
                                        fontWeight: 800,
                                        background: `linear-gradient(135deg, ${previewPrimary} 0%, ${previewSecondary} 100%)`,
                                            boxShadow: '0 0 0 1px rgba(255,255,255,0.1), 0 16px 32px var(--accent-shadow)',
                                            '&.Mui-disabled': {
                                                color: 'rgba(255,255,255,0.72)',
                                                background: 'var(--button-disabled-bg)',
                                            },
                                    }}
                                >
                                    {t.startChatting}
                                </Button>
                            </Box>
                        ) : (
                            <>
                                <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2.25 }}>
                                    <Typography variant='overline' sx={{ color: 'var(--text-muted)', fontWeight: 800, letterSpacing: '0.12em' }}>
                                        {tr('wizardProgress', { current: activeStep + 1, total: stepTitles.length })}
                                    </Typography>
                                    <LinearProgress
                                        variant='determinate'
                                        value={completionRatio}
                                        sx={{
                                            mt: 1,
                                            height: 8,
                                            borderRadius: '999px',
                                            backgroundColor: 'var(--surface-muted)',
                                            '& .MuiLinearProgress-bar': {
                                                borderRadius: '999px',
                                                background: `linear-gradient(135deg, ${previewPrimary} 0%, ${previewSecondary} 100%)`,
                                            },
                                        }}
                                    />
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant='overline' sx={{ color: 'var(--text-muted)', fontWeight: 800, letterSpacing: '0.12em' }}>
                                        {stepTitles[activeStep]}
                                    </Typography>
                                </Box>

                                <Box
                                    key={activeStep}
                                    sx={{
                                        flex: 1,
                                        minHeight: 0,
                                        maxHeight: { xs: 'none', md: 420 },
                                        overflowY: 'auto',
                overscrollBehavior: 'contain',
                                        pr: { xs: 0, md: 0.5 },
                                        mr: { xs: 0, md: -0.5 },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: { xs: 'flex-start', md: 'center' },
                                        animation: 'wizardFade 0.28s ease',
                                        pb: { xs: 1.5, md: 0 },
                                        scrollbarWidth: 'thin',
                                        '@keyframes wizardFade': {
                                            from: { opacity: 0, transform: 'translateY(10px)' },
                                            to: { opacity: 1, transform: 'translateY(0)' },
                                        },
                                        '&::-webkit-scrollbar': {
                                            width: 7,
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            borderRadius: '999px',
                                            backgroundColor: 'rgba(148,163,184,0.55)',
                                        },
                                    }}
                                >
                                    {renderStepContent()}
                                </Box>

                                <Box
                                    sx={{
                                        mt: { xs: 2, md: 3 },
                                        pt: { xs: 2, md: 2.5 },
                                        borderTop: '1px solid var(--border-color)',
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1.25,
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        position: { xs: 'sticky', md: 'static' },
                                        bottom: { xs: 0, md: 'auto' },
                                        zIndex: 2,
                                        backgroundColor: { xs: 'rgba(255,255,255,0.96)', md: 'transparent' },
                                        backdropFilter: { xs: 'blur(10px)', md: 'none' },
                                        pb: { xs: 'calc(env(safe-area-inset-bottom, 0px) + 4px)', md: 0 },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
                                        <Button
                                            type='button'
                                            onClick={goBack}
                                            disabled={activeStep === 0 || isSaving}
                                            startIcon={<ArrowBack />}
                                            sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 700, color: 'var(--text-strong)', minHeight: 48, flex: { xs: 1, sm: '0 1 auto' } }}
                                        >
                                            {t.back}
                                        </Button>
                                        <Button
                                            type='button'
                                            onClick={handleSkip}
                                            disabled={isSaving || isUploadingLogo}
                                            endIcon={<SkipNext />}
                                            sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 700, color: 'var(--text-strong)', minHeight: 48, flex: { xs: 1, sm: '0 1 auto' } }}
                                        >
                                            {activeStep === stepTitles.length - 1 ? t.finishNow : t.skip}
                                        </Button>
                                    </Box>

                                    <Button
                                        type='button'
                                        onClick={activeStep === stepTitles.length - 1 ? finishSetup : goNext}
                                        variant='contained'
                                        disabled={isSaving || isUploadingLogo}
                                        endIcon={isSaving ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <ArrowForward />}
                                        sx={{
                                            minHeight: 52,
                                            px: 2.75,
                                            width: { xs: '100%', sm: 'auto' },
                                            borderRadius: '16px',
                                            textTransform: 'none',
                                            fontWeight: 800,
                                            background: `linear-gradient(135deg, ${previewPrimary} 0%, ${previewSecondary} 100%)`,
                                            boxShadow: '0 0 0 1px rgba(255,255,255,0.1), 0 16px 32px var(--accent-shadow)',
                                            '&.Mui-disabled': {
                                                color: 'rgba(255,255,255,0.72)',
                                                background: 'var(--button-disabled-bg)',
                                            },
                                        }}
                                    >
                                        {activeStep === stepTitles.length - 1 ? t.finishSetup : t.continue}
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </Paper>
        </Modal>
    );
}

export default ChatbotSetupWizard;